/**
 * Tipos de dominio del Panel Comerciales (vista del frontend).
 *
 * La distribución de un comercial tiene DOS EJES INDEPENDIENTES, ambos
 * provenientes del snapshot del backend:
 *
 *  - PRODUCTO (`meta.products`): catálogo de `pipedrive_products`; la `key` de
 *    cada entrada es el `product_code` (DESTACADO, PRIME, ELITE, SMART_LEADS,
 *    ANUNCIO_SIMPLE, OPORTUNIDADES_ILIMITADAS). Mapea a filas dimension=PRODUCT
 *    (dim_key=product_code) en `pipedrive_sellers_distribution`.
 *
 *  - TIPO DE FLUJO (`meta.flowTypes`): la `key` es el `pipeline_key`
 *    (RENOVACION, NUEVO_CLIENTE, CARRITO, RECUPERACION, INTERES_FUTURO). Mapea
 *    a filas dimension=FLOW (dim_key=pipeline_key).
 *
 * Cada eje suma 100 de forma independiente. El editor (modal de 2 tabs) edita
 * ambos por separado y emite `{ products, flows }`.
 */

/** Entrada del catálogo de productos. `key` = `product_code`. */
export interface ProductDef {
    key: string;
    label: string;
}

/** Entrada del catálogo de tipos de flujo. `key` = `pipeline_key`. */
export interface FlowTypeDef {
    key: string;
    label: string;
}

/** Un eje genérico de distribución (producto o flujo) para reusar el editor. */
export interface DistributionAxisDef {
    key: string;
    label: string;
}

export type CommercialStatus = 'Activo' | 'Inactivo' | 'Bloqueado';
export const COMMERCIAL_STATUS_OPTIONS: CommercialStatus[] = [
    'Activo',
    'Inactivo',
    'Bloqueado',
];

/**
 * Origen del status del seller:
 *  - `panel`: lo decidimos desde el panel (vive en pipedrive_sellers_status).
 *  - `pipedrive`: lo refleja la sincronización con Pipedrive (active_flag / state).
 *  - `undefined` cuando el seller está Activo.
 */
export type CommercialStatusSource = 'panel' | 'pipedrive';

/**
 * Motivos válidos para la novedad/ausencia del panel. Coincide con el ENUM
 * lógico que recibe el backend (`SellerConfigPatch.state`) excluyendo
 * "activo" (que se usa para limpiar la novedad).
 */
export const TEMPORAL_INACTIVATION_REASONS = [
    { value: 'vacaciones', label: 'Vacaciones' },
    { value: 'incapacidad', label: 'Incapacidad médica' },
    { value: 'capacitacion', label: 'Capacitación' },
    { value: 'permisos', label: 'Permisos' },
] as const;

export interface Commercial {
    id: number;
    name: string;
    team: string;
    active: boolean;
    status: CommercialStatus;
    /** De dónde viene el status actual; permite mostrar badges y decidir qué se puede editar. */
    statusSource?: CommercialStatusSource;
    capacity: number;
    calcLeads: number;
    /**
     * Capacidad # Deals: max deals open en paralelo asignados por el cronjob
     * de scoring. Antes UI "leads efectivos"; el campo conserva el nombre por
     * compatibilidad con el contrato backend.
     */
    leadsEfectivos: number;
    /**
     * Deals ASIGNADOS al comercial en el mes calendario vigente (cuota mensual
     * consumida). Cupo restante del mes = leadsEfectivos − asignadosMes. Lo
     * calcula y persiste el cronjob de scoring; no editable.
     */
    asignadosMes?: number;
    /** Total de deals del seller en `pipedrive_deals` (informativo, no editable). */
    totalDeals?: number;
    /** Deals en status OPEN (informativo, no editable). */
    openDeals?: number;
    /** Deals en status LOST/CLOSED — excluye WON desde 2026-07 (informativo). */
    closedDeals?: number;
    /** Deals en status WON (informativo, no editable). */
    wonDeals?: number;
    /**
     * EJE PRODUCTO. Distribución/afinidad por producto. Claves = `product_code`,
     * valores en porcentaje (deben sumar 100 de forma independiente del eje
     * flujo). `api.ts` lo normaliza desde `specialty` cuando el backend aún no
     * lo envía. Opcional sólo por retrocompatibilidad con callers/fixtures.
     */
    products?: Record<string, number>;
    /**
     * EJE FLUJO. Distribución por tipo de flujo. Claves = `pipeline_key`
     * (RENOVACION, NUEVO_CLIENTE, CARRITO, RECUPERACION, INTERES_FUTURO),
     * valores en porcentaje (deben sumar 100 de forma independiente del eje
     * producto). Opcional por retrocompatibilidad: el backend puede no enviarlo
     * todavía.
     */
    flows?: Record<string, number>;
    /**
     * Legacy (transición): Alias temporal de `products`. `api.ts` mantiene ambos campos
     * sincronizados al normalizar el snapshot. Eliminar cuando todos los
     * consumidores lean `products`.
     */
    specialty?: Record<string, number>;
    goal: string;
    absence: string;
    tempBlockUntil?: string;
    notes?: string;
    /**
     * Reglas de Marketing (jul-2026): participa en la rotación de deals
     * NUEVOS de campaña (switch "Campañas" de la tabla, editable por las
     * líderes). Los deals existentes que se complementan no dependen de
     * este flag. Opcional por retrocompatibilidad con fixtures.
     */
    marketingEnabled?: boolean;
    /** Toggle "Renovación": recibe o no deals del flujo de renovación. */
    renovationEnabled?: boolean;
    /**
     * Campos TRANSIENT usados por `CommercialEditDrawer` cuando el usuario
     * cambia el status: el drawer rellena `statusReason` (valor máquina) y
     * `statusSince`, y `buildCommercialPatch` los traduce a
     * `state` / `state_since` para el backend. No vienen del snapshot.
     */
    statusReason?: string;
    statusSince?: string;
}

export interface Team {
    /** id persistido en `pipedrive_teams`. Ausente sólo para equipos derivados legacy. */
    id?: number;
    name: string;
    leader: string;
    members: number;
    goal: string;
    active: boolean;
}

export type VipStatus = CommercialStatus;

export interface VipAccount {
    id: string;
    client: string;
    email: string;
    owner: string;
    team: string;
    reason: string;
    validity: string;
    status: VipStatus;
}

export type NotifType = 'critical' | 'urgent' | 'info' | 'informative';

export const NOTIF_TYPE_LABEL: Record<NotifType, string> = {
    critical: 'Alerta crítica',
    urgent: 'Alerta urgente',
    informative: 'Alerta informativa',
    info: 'Info',
};

export interface Notification {
    id: number;
    date: string;
    type: NotifType;
    message: string;
    team: string;
}

export interface AuditEntry {
    date: string;
    user: string;
    action: string;
    detail: string;
}

export const buildEmptySpec = (axis: DistributionAxisDef[]): Record<string, number> =>
    Object.fromEntries(axis.map((p) => [p.key, 0]));

export const buildDefaultSpec = (axis: DistributionAxisDef[]): Record<string, number> => {
    const spec = buildEmptySpec(axis);
    if (axis.length > 0) spec[axis[0].key] = 100;
    return spec;
};

export const sumSpec = (s: Record<string, number>): number =>
    Object.values(s).reduce((acc, v) => acc + (v || 0), 0);

export const sameSpec = (a: Record<string, number>, b: Record<string, number>): boolean => {
    const allKeys = Object.keys(a).concat(Object.keys(b).filter((k) => !(k in a)));
    return allKeys.every((k) => (a[k] || 0) === (b[k] || 0));
};

export const effectiveLeads = (c: Pick<Commercial, 'calcLeads' | 'capacity'>): number =>
    Math.round((c.calcLeads * c.capacity) / 100);

const PALETTE = [
    '#1E7BFF', '#7C3AED', '#10B981', '#F59E0B',
    '#EC4899', '#F97316', '#06B6D4', '#8B5CF6',
    '#EF4444', '#14B8A6', '#6366F1', '#84CC16',
];

/** Color por índice para el eje PRODUCTO. */
export const productColor = (index: number): string =>
    PALETTE[index % PALETTE.length];

/**
 * Color por índice para el eje FLUJO. Usa un offset en la paleta para que los
 * dos ejes no se vean idénticos cuando se muestran lado a lado.
 */
export const flowTypeColor = (index: number): string =>
    PALETTE[(index + 6) % PALETTE.length];

/** Legacy (transición): Alias histórico; usar {@link flowTypeColor} o {@link productColor}. */
export const axisColor = productColor;
